import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Phone,
  Clock,
  Loader,
  MessageCircle,
  AlertTriangle,
  Play,
  CheckCircle,
  Shield
} from 'lucide-react';

// Import Daily.co React hooks
import {
  DailyAudio,
  DailyProvider,
  DailyVideo,
  useCallObject,
  useDaily,
  useDailyEvent,
  useLocalParticipant,
  useParticipantIds
} from '@daily-co/daily-react';

// Import services and utilities
import {
  createPipecatConversation,
  createInterview,
  updateInterviewStatus,
  endPipecatConversation,
  generateCandidateInsights,
  generatePracticeInsights,
  getInterviewStatus
} from '../../services/interviewService';
import { submitAssessment } from '../../services/assessmentService';
import { getBrowserName, isBrowserSupported } from '../../utils/browser';
import TranscriptionPanel from './TranscriptionPanel';
import { FloatingParticles } from '../../components/animations/FloatingParticles';
import { API_CONFIG } from '../../config/api';

// Import types
import type {
  CustomInterviewSection,
  VideoInterviewStatus,
  PipecatConversationRequest,
  PipecatAppMessageData,
  TavusAppMessageData,
  DailyAppMessageCallback,
  VideoInterviewRef
} from '../../types/interview';

// Message interface for conversation history
interface ConversationMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: number;
  isComplete?: boolean;
}

interface CustomVoiceInterviewProps {
  onEndInterview: () => void;
  sections: CustomInterviewSection[];
  interviewName?: string;
  description?: string;
  maxDuration?: number;
  userName?: string;
  aiName?: string;

  // Backend configuration
  pipecatApiUrl?: string;
  agentName?: string;
  authToken?: string;
  s3BucketName?: string;
  s3BucketRegion?: string;
  awsAssumeRoleArn?: string;
  fastapiURL?: string;
  userId?: string;
  userCV?: string;
  interviewId?: string;
  sessionId?: string;  // Add session ID prop

  // Interview settings
  isTestInterview?: boolean;
  isPracticeInterview?: boolean;
  requireSignIn?: boolean;
  userEmail?: string;
  jobId?: string;
  language?: "English" | "Vietnamese" | "FirstMove";
  interviewLanguage?: string;
  voice?: "male" | "female";
  customGreeting?: string;
  concludingStatements?: string;
  idleTimeoutMessage?: string;
}

// Provider wrapper component
const DailyInterviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Create call object at the provider level
  const callObject = useCallObject({});

  return (
    <DailyProvider callObject={callObject}>
      {children}
    </DailyProvider>
  );
};

// Main component that uses Daily.co context
export const CustomVoiceInterview: React.FC<CustomVoiceInterviewProps> = (props) => {
  return (
    <DailyInterviewProvider>
      <CustomVoiceInterviewInner {...props} />
    </DailyInterviewProvider>
  );
};

// Inner component that uses Daily.co hooks
const CustomVoiceInterviewInner: React.FC<CustomVoiceInterviewProps> = ({
  onEndInterview,
  sections,
  interviewName = "MTAI AI Interview",
  maxDuration = 20,
  userName = "Candidate",
  aiName = "MTAI AI",

  // Backend config with defaults (now using your environment variables)
  pipecatApiUrl = import.meta.env.VITE_PIPECAT_API_URL || 'https://api.pipecat.daily.co',
  agentName = import.meta.env.VITE_PIPECAT_AGENT_NAME || 'my-staging-agent',
  authToken = import.meta.env.VITE_PIPECAT_AUTH_TOKEN || '',
  s3BucketName = import.meta.env.VITE_AWS_S3_BUCKET_NAME || '',
  s3BucketRegion = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'ap-southeast-1',
  awsAssumeRoleArn = import.meta.env.VITE_AWS_ASSUME_ROLE_ARN || '',
  fastapiURL = import.meta.env.VITE_PIPECAT_CALLBACK_URL || 'https://nddwrvn1-5000.asse.devtunnels.ms/api',
  userId = 'demo-user',
  userCV = '',
  interviewId = 'demo-interview',
  sessionId,  // Add session ID to destructured props

  // Interview settings (keeping for future use)
  isTestInterview = false,
  isPracticeInterview = false,
  requireSignIn = false,
  userEmail = '',
  jobId = '',
  language = "English",
  interviewLanguage = "en-US",
  voice = "male",
  customGreeting,
  concludingStatements = "Thank you for your time and thoughtful responses. Have a great day!",
  idleTimeoutMessage = "It seems like you're busy right now. Have a nice day!"
}) => {
  // Interview state
  const [status, setStatus] = useState<VideoInterviewStatus>("idle");
  const [isActive, setIsActive] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [interviewStep, setInterviewStep] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(maxDuration * 60);
  const [conversationUrl, setConversationUrl] = useState<string | null>(null);
  const [conversationToken, setConversationToken] = useState<string | null>(null);
  const [interviewStartTime, setInterviewStartTime] = useState<number>(0);
  const [isRecordingStarting, setIsRecordingStarting] = useState<boolean>(false);
  const [conversationStarted, setConversationStarted] = useState(false);

  // UI state for component creation tracking
  const conversationCreatedRef = useRef<boolean>(false);

  // Media state
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(false); // Ref to avoid stale closure in callbacks
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [micAccessGranted, setMicAccessGranted] = useState(false);
  const [cameraAccessGranted, setCameraAccessGranted] = useState(false);
  const [permissionRequested, setPermissionRequested] = useState(false);
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(true);

  // UI state
  const [apiErrorMessage, setApiErrorMessage] = useState<string>("");
  const [showVideoUI, setShowVideoUI] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isStartingRecording, setIsStartingRecording] = useState<boolean>(false);
  const [countdownSeconds, setCountdownSeconds] = useState<number>(180); // 3 minutes
  const [countdownActive, setCountdownActive] = useState<boolean>(false);
  const [interviewExpired, setInterviewExpired] = useState<boolean>(false);
  const [blockInterview, setBlockInterview] = useState<boolean>(false);
  const [showLeaveCallPopup, setShowLeaveCallPopup] = useState<boolean>(false);
  const [isInterviewEnding, setIsInterviewEnding] = useState<boolean>(false);
  const [isCompletingInterview, setIsCompletingInterview] = useState<boolean>(false);

  // Refs for timeout management
  const autoEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Audio state
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [aiJoined, setAiJoined] = useState(false);
  const [dailyJoined, setDailyJoined] = useState(false);

  // Device management state
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioInputId, setSelectedAudioInputId] = useState<string>("");
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState<string>("");
  const [selectedVideoId, setSelectedVideoId] = useState<string>("");

  // Browser compatibility
  const [isBrowserCompatible, setIsBrowserCompatible] = useState(true);

  // Conversation state
  const [showTranscript, setShowTranscript] = useState(true);
  const [conversationMessages, setConversationMessages] = useState<ConversationMessage[]>([]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const sectionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Use Daily.co hooks for call management and audio detection
  const callObject = useDaily();
  const localParticipant = useLocalParticipant();
  const remoteParticipantIds = useParticipantIds({ filter: "remote" });

  // Audio level detection for user speaking
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);

  // Track if user is actually talking (above threshold)
  const audioLevelRef = useRef(0);

  // Helper function to check if message is just YES or NO
  const isYesNoMessage = useCallback((content: string): boolean => {
    if (!content) return false;
    const normalized = content.trim().replace(/[.,!?;:]*/g, '').toUpperCase();
    return normalized === 'YES' || normalized === 'NO';
  }, []);

  // Helper function to detect if AI is giving an ending response
  const isEndingMessage = useCallback((content: string): boolean => {
    if (!content) return false;
    const normalized = content.toLowerCase();
    const endingKeywords = [
      'thank you for your time',
      'we\'ll be in touch',
      'have a great day',
      'thank you for the interview',
      'we will contact you',
      'good luck',
      'it was great learning about',
      'we appreciate your time',
      'next steps',
      'thank you for sharing'
    ];
    return endingKeywords.some(keyword => normalized.includes(keyword));
  }, []);

  useEffect(() => {
  }, [callObject]);

  // Disable body scrolling when interview is active
  useEffect(() => {
    // Save current overflow style
    const originalOverflow = document.body.style.overflow;

    // Disable body scrolling
    document.body.style.overflow = 'hidden';

    // Cleanup function to restore original overflow
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []); // Empty dependency array means this runs once on mount and cleanup on unmount

  // Sync isMuted state to ref (so callbacks always have latest value)
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Clear speaking indicator when muted
  useEffect(() => {
    if (isMuted && isUserSpeaking) {
      setIsUserSpeaking(false);
    }
  }, [isMuted, isUserSpeaking]);

  // Check Interview Status (prevent joining if already completed/blocked)
  const checkInterviewStatus = useCallback(async () => {
    try {
      if (requireSignIn && interviewId !== 'demo-interview') {
        const res = await getInterviewStatus(interviewId);

        if (res?.data?.status === "Pending Interview") {
          setBlockInterview(false);
        } else {
          setBlockInterview(true);

          // Set specific error messages based on status
          if (res?.data?.status === "None") {
            setApiErrorMessage("This interview session has expired. You did not join within the allocated time.");
          } else if (res?.data?.status === "Interviewer Ready") {
            setApiErrorMessage("It looks like you refreshed the page, which ended the session. Please request to retake the interview");
          } else {
            setApiErrorMessage(`Interview access blocked (Status: ${res?.data?.status}). You may need to request a retake.`);
          }
        }
      } else {
        setBlockInterview(false);
      }
    } catch (error: any) {
      setBlockInterview(true);
      setApiErrorMessage("Could not verify interview status. Please try refreshing or contact support.");
    }
  }, [requireSignIn, sessionId]);

  useEffect(() => {
    // Check status once when the component mounts
    checkInterviewStatus();
  }, [checkInterviewStatus]);  // Daily.co event handlers
  useDailyEvent(
    'participant-joined',
    useCallback((event) => {
      if (event.participant.session_id !== localParticipant?.session_id) {
        setAiJoined(true);
      }
    }, [localParticipant?.session_id])
  );

  useDailyEvent(
    'joined-meeting',
    useCallback(() => {
      setDailyJoined(true);

      // In test mode, simulate AI joining after a short delay
      if (isTestInterview) {
        setTimeout(() => {
          setAiJoined(true);
        }, 2000);
      }
    }, [isTestInterview])
  );

  useDailyEvent(
    'local-audio-level',
    useCallback((event) => {
      audioLevelRef.current = event.audioLevel;
      // Threshold for detecting speech (adjust as needed)
      const speechThreshold = 0.01;
      const speaking = event.audioLevel > speechThreshold;

      // Handle user speaking state changes - only if NOT muted (use ref for latest value)
      const currentlyMuted = isMutedRef.current;

      if (!currentlyMuted) {
        // Only update speaking state when NOT muted
        if (speaking !== isUserSpeaking) {
          setIsUserSpeaking(speaking);
        }
      } else {
        // If muted and we're currently showing speaking, clear it immediately
        if (isUserSpeaking) {
          setIsUserSpeaking(false);
        }
      }
    }, [isUserSpeaking])
  );

  useDailyEvent(
    'error',
    useCallback((event) => {
      setApiErrorMessage(`Connection error: ${event.errorMsg || 'Failed to connect to interview session'}`);
      setIsStartingRecording(false);
    }, [])
  );

  // Device enumeration and setup
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      const audioOutputs = devices.filter(device => device.kind === 'audiooutput');
      const videoInputs = devices.filter(device => device.kind === 'videoinput');

      setAudioInputDevices(audioInputs);
      setAudioOutputDevices(audioOutputs);
      setVideoDevices(videoInputs);

      // Set default devices if none selected
      if (!selectedAudioInputId && audioInputs.length > 0) {
        setSelectedAudioInputId(audioInputs[0].deviceId);
      }
      if (!selectedAudioOutputId && audioOutputs.length > 0) {
        setSelectedAudioOutputId(audioOutputs[0].deviceId);
      }
      if (!selectedVideoId && videoInputs.length > 0) {
        setSelectedVideoId(videoInputs[0].deviceId);
      }
    } catch (error) {
    }
  }, [selectedAudioInputId, selectedAudioOutputId, selectedVideoId]);

  // Setup media stream with device constraints
  const setupMediaStream = useCallback(async (
    audioDeviceId: string = selectedAudioInputId,
    videoDeviceId: string = selectedVideoId
  ) => {
    try {
      // Stop existing stream
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }

      const constraints: MediaStreamConstraints = {
        audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
        video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setMediaStream(stream);
      setCameraAccessGranted(true);
      setMicAccessGranted(true);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // No need for separate speech detection with Daily.co
    } catch (error) {
      // In test mode, allow proceeding even without camera/mic
      if (isTestInterview) {
        setCameraAccessGranted(true);
        setMicAccessGranted(true);
        setApiErrorMessage('Test mode: Camera and microphone access simulated');
      } else {
        setCameraAccessGranted(false);
        setMicAccessGranted(false);
        setApiErrorMessage('Failed to access camera and microphone. Please grant permissions and try again.');
      }
    }
  }, [selectedAudioInputId, selectedVideoId, isTestInterview]);

  // Check if devices are available before requesting permissions
  const checkDevicesAvailable = useCallback(async (): Promise<{ hasCamera: boolean; hasMicrophone: boolean }> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasCamera = devices.some(device => device.kind === 'videoinput');
      const hasMicrophone = devices.some(device => device.kind === 'audioinput');
      return { hasCamera, hasMicrophone };
    } catch (error) {
      return { hasCamera: false, hasMicrophone: false };
    }
  }, []);

  // User-initiated permission request function
  const requestPermissions = useCallback(async () => {
    try {
      setPermissionRequested(true);
      setApiErrorMessage('');

      // First check if devices are available
      const { hasCamera, hasMicrophone } = await checkDevicesAvailable();

      if (!hasCamera && !hasMicrophone) {
        setApiErrorMessage('No camera or microphone found. Please connect your devices and refresh the page.');
        setPermissionRequested(false); // Reset so button stays visible
        return;
      }

      if (!hasCamera) {
        setApiErrorMessage('No camera found. Please connect a camera and refresh the page.');
        setPermissionRequested(false); // Reset so button stays visible
        return;
      }

      if (!hasMicrophone) {
        setApiErrorMessage('No microphone found. Please connect a microphone and refresh the page.');
        setPermissionRequested(false); // Reset so button stays visible
        return;
      }

      const constraints: MediaStreamConstraints = {
        audio: selectedAudioInputId ? { deviceId: { exact: selectedAudioInputId } } : true,
        video: selectedVideoId ? { deviceId: { exact: selectedVideoId } } : true
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setMediaStream(stream);
      setCameraAccessGranted(true);
      setMicAccessGranted(true);
      setShowPermissionPrompt(false);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

    } catch (error) {
      setCameraAccessGranted(false);
      setMicAccessGranted(false);

      if (isTestInterview) {
        setCameraAccessGranted(true);
        setMicAccessGranted(true);
        setShowPermissionPrompt(false);
        setApiErrorMessage('Test mode: Camera and microphone access simulated');
      } else {
        const errorName = (error as any)?.name || '';
        let userFriendlyMessage = 'Unable to access camera and microphone. ';

        if (errorName === 'NotAllowedError') {
          userFriendlyMessage += 'Please allow access when prompted and try again.';
          setPermissionRequested(false); // Reset so user can try again
        } else if (errorName === 'NotFoundError') {
          userFriendlyMessage += 'No camera or microphone found. Please connect your devices and refresh the page.';
          setPermissionRequested(false); // Reset so button stays visible
        } else if (errorName === 'NotReadableError') {
          userFriendlyMessage += 'Camera or microphone is being used by another application.';
          setPermissionRequested(false); // Reset so user can try again
        } else {
          userFriendlyMessage += 'Please check your browser settings and try again.';
          setPermissionRequested(false); // Reset so user can try again
        }

        setApiErrorMessage(userFriendlyMessage);
      }
    }
  }, [selectedAudioInputId, selectedVideoId, isTestInterview, checkDevicesAvailable]);

  // Device selection handlers
  const handleMicrophoneSelect = useCallback((device: MediaDeviceInfo) => {
    if (device.deviceId !== selectedAudioInputId) {
      setSelectedAudioInputId(device.deviceId);
      setupMediaStream(device.deviceId, selectedVideoId);
    }
  }, [selectedAudioInputId, selectedVideoId, setupMediaStream]);

  const handleSpeakerSelect = useCallback((device: MediaDeviceInfo) => {
    setSelectedAudioOutputId(device.deviceId);
  }, []);

  const handleCameraSelect = useCallback((device: MediaDeviceInfo) => {
    if (device.deviceId !== selectedVideoId) {
      setSelectedVideoId(device.deviceId);
      setupMediaStream(selectedAudioInputId, device.deviceId);
    }
  }, [selectedAudioInputId, selectedVideoId, setupMediaStream]);

  // Check if interview can start
  const canStartInterview = cameraAccessGranted && micAccessGranted && aiJoined && dailyJoined && !blockInterview && !interviewExpired;

  useEffect(() => {
  }, [cameraAccessGranted, micAccessGranted, aiJoined, dailyJoined, blockInterview, interviewExpired, canStartInterview]);

  // Get button tooltip
  const getButtonTooltip = (): string | undefined => {
    if (canStartInterview) return undefined;
    if (blockInterview) return "Interview cannot be started due to system restrictions";
    if (interviewExpired) return "Interview session has expired";
    if (!cameraAccessGranted || !micAccessGranted) return "Please grant camera and microphone permissions";
    if (!aiJoined) return "Waiting for AI interviewer to join";
    if (!dailyJoined) return "Connecting to interview session";
    return "Please ensure all requirements are met";
  };

  // Create conversation with Pipecat and backend - following old component pattern
  const createConversation = useCallback(async () => {
    // Prevent creation if blocked, already created, or in wrong state
    if (blockInterview || conversationCreatedRef.current || (status !== 'idle' && status !== 'error')) {
      return;
    }

    conversationCreatedRef.current = true; // Mark as attempting creation
    setStatus('connecting');
    setApiErrorMessage('');

    try {
      // Create Pipecat conversation
      const conversationData: PipecatConversationRequest = {
        createDailyRoom: true,
        dailyRoomProperties: {
          max_participants: 2,
          exp: Math.floor(Date.now() / 1000) + 1500, // 25 minutes
          eject_at_room_exp: true,
          enable_recording: "cloud",
          start_video_off: false
        },
        body: {
          interviewName: interviewName || `Interview - ${userId} - ${new Date().toISOString()}`,
          interviewContext: `This is an interview for the position: ${interviewName}\n\nJob ID: ${jobId}\n\nInterview Sections:\n${sections.map(s => s.instruction).join('\n\n')}\n\nIMPORTANT BEHAVIORAL GUIDELINES:\n- Always refer to the position by its proper name (${interviewName}) when speaking to the candidate, not by the job ID\n- Be patient and give candidates time to think before answering (wait at least 10-15 seconds of silence before prompting)\n- If you can't hear clearly or the response seems incomplete, politely ask for clarification: "I didn't catch that clearly, could you please repeat that?"\n- Allow natural pauses for thinking - don't rush to the next question immediately\n- If a candidate takes time to respond, use encouraging phrases like "Take your time" or "No rush, think it through"\n- Be conversational and understanding - this should feel like a natural discussion, not a rapid-fire questionnaire\n- If unclear about an answer, ask follow-up questions rather than moving on immediately`,
          aiName: aiName,
          userName: userName,
          customGreeting: customGreeting || "", // Let AI handle greeting naturally to avoid duplication
          userId: userId,
          userCV: userCV,
          fastapiURL: fastapiURL,
          conversationId: sessionId as string,
          s3BucketName: s3BucketName,
          s3BucketRegion: s3BucketRegion,
          awsAssumeRoleArn: awsAssumeRoleArn,
          isTestInterview: false,
          concludingStatements: concludingStatements,
          language: interviewLanguage,
          voice: voice,
          idleTimeoutMessage: idleTimeoutMessage || "I notice you might need more time to think, or there might be a technical issue. Feel free to take a moment and let me know when you're ready to continue, or we can wrap up if needed."
        }
      };

      const response = await createPipecatConversation(agentName, conversationData, authToken);

      setConversationUrl(response.dailyRoom);
      setConversationToken(response.dailyToken);
      setStatus('active'); // Ready for Daily join

    } catch (error: any) {
      conversationCreatedRef.current = false; // Allow retry if needed
      setApiErrorMessage(error.response?.data?.message || 'Failed to create Pipecat conversation. Please check configuration or network.');
      setStatus('error');
    }
  }, [
    blockInterview, status, authToken, agentName,
    interviewName, sections, aiName, userName, customGreeting, fastapiURL,
    s3BucketName, s3BucketRegion, awsAssumeRoleArn, userId, isTestInterview,
    userCV, concludingStatements, interviewLanguage, voice, idleTimeoutMessage,
    sessionId, userEmail, jobId
  ]);

  // Daily.co join/leave effect - following old component pattern
  useEffect(() => {
    if (!callObject) {
      return; // Daily not initialized
    }

    const currentState = callObject.meetingState();

    // Join Condition: Status is active, URL is available, and not already joined/joining
    if (status === 'active' && conversationUrl && conversationToken && (currentState === 'new' || currentState === 'left-meeting')) {
      callObject.join({
        url: conversationUrl,
        token: conversationToken,
      })
        .then(() => {
          setDailyJoined(true); // Update local state

          callObject.setLocalAudio(false) //mute local audio until user joins room

          // If showing the main UI, ensure devices are set in Daily
          if (showVideoUI) {
            callObject.setInputDevicesAsync({
              audioDeviceId: selectedAudioInputId || undefined,
              videoDeviceId: selectedVideoId || undefined,
            }).catch(() => {});

            if (selectedAudioOutputId) {
              callObject.setOutputDeviceAsync({ outputDeviceId: selectedAudioOutputId })
                .catch(() => {});
            }
          }
        })
        .catch((error) => {
          setApiErrorMessage(`Failed to join video call: ${error.message}`);
          setStatus('error');
        });
    }

    // Leave Condition: Status change away from active or error
    if ((status === 'completed' || status === 'error') && currentState === 'joined-meeting') {
      callObject.leave().catch(() => {});
    }
  }, [status, aiJoined, conversationUrl, callObject, showVideoUI, dailyJoined, selectedAudioInputId, selectedVideoId, selectedAudioOutputId, userId, conversationToken]);

  // Create interview contexts for step progression
  const interviewContexts = React.useMemo<string[]>(() => {
    return sections.map((section) => `Current Section Instructions:\n${section?.instruction}\n\n`)
  }, [sections]);

  // Daily Event Handlers - following old component pattern
  const handleAppMessage: DailyAppMessageCallback = useCallback(
    (event: any) => {
      const pipecat_event = event.data as PipecatAppMessageData

      if (pipecat_event.type === "bot-tts-started") {
        // Bot is streaming text
        setIsAiSpeaking(true);
      }
      else if (pipecat_event.type === "bot-tts-text") {
        // Handle streaming AI text for transcription
        if (!isUserSpeaking) {
          const streamingText = pipecat_event.data?.text;
          if (streamingText && !isYesNoMessage(streamingText)) {
            // Update or create streaming message
            setConversationMessages(prevMessages => {
              const hasIncompleteMessage = prevMessages.some(msg => !msg.isComplete && msg.type === 'ai');

              if (hasIncompleteMessage) {
                return prevMessages.map(msg =>
                  (!msg.isComplete && msg.type === 'ai')
                    ? { ...msg, content: msg.content + streamingText }
                    : msg
                );
              } else {
                const newMessage: ConversationMessage = {
                  id: Date.now().toString(),
                  type: 'ai',
                  content: streamingText,
                  timestamp: Date.now(),
                  isComplete: false
                };
                return [...prevMessages, newMessage];
              }
            });
          }
        }
      }
      else if (pipecat_event.type === "bot-stopped-speaking") {
        // Bot has finished speaking
        setIsAiSpeaking(false);

        // Mark current AI message as complete and check for ending
        setConversationMessages(prevMessages => {
          const message = prevMessages.find(msg => !msg.isComplete && msg.type === 'ai');
          if (message && isYesNoMessage(message.content)) {
            return prevMessages.filter(msg => msg.id !== message.id);
          }

          // Check if this message indicates the interview is ending
          if (message && isEndingMessage(message.content)) {
            setIsInterviewEnding(true);
            // Show the "You may leave the call now" popup after a brief delay
            setTimeout(() => {
              setShowLeaveCallPopup(true);
            }, 2000); // 2 second delay to let the message fully display

            // Automatically end the interview after giving user time to read the message
            autoEndTimeoutRef.current = setTimeout(() => {
              if (!isCompletingInterview) {
                endAiCall();
              }
            }, 5000); // 5 second delay for user to see thank you message
          }

          return prevMessages.map(msg =>
            (!msg.isComplete && msg.type === 'ai')
              ? { ...msg, isComplete: true }
              : msg
          );
        });
      }
      else if (pipecat_event.type === "user-started-speaking") {
        // User started speaking - only if NOT muted (use ref for latest value)
        if (!isMutedRef.current) {
          setIsUserSpeaking(true);
        }
      }
      else if (pipecat_event.type === "user-stopped-speaking") {
        // User stopped speaking
        setIsUserSpeaking(false);
      }
      else if (pipecat_event.type === "user-transcript") {
        // User speech transcript received
        const userText = pipecat_event.data?.text;
        if (userText && userText.trim()) {
          const newMessage: ConversationMessage = {
            id: Date.now().toString(),
            type: 'user',
            content: userText.trim(),
            timestamp: Date.now(),
            isComplete: true
          };
          setConversationMessages(prevMessages => [...prevMessages, newMessage]);
        }
      }
      else if (pipecat_event.type === "conversation.test_interview_start") {
        setShowConfirmationModal(false);

        if (callObject) {
          callObject.setLocalAudio(true);

          // Mark interview start time
          setInterviewStartTime(Date.now());

          setShowVideoUI(true); // Show the main VideoContainer UI
          setIsActive(true); // Set interview as active to hide loading overlay
          setIsRecordingStarting(false);

          // Set initial context (Step 0)
          updateConversationContext(interviewContexts[0]);
          setInterviewStep(0); // Explicitly set step to 0
          // wait 1 second
          setTimeout(() => {
            makeAiSay(`${customGreeting ? customGreeting : `Hey, thanks for joining the call today. Are you ready for your ${interviewName}?`}`)
          }, 1000)
        }
      }

      // Handle Tavus messages as well
      const tavusMessage = event.data as TavusAppMessageData;
      if (!tavusMessage || !tavusMessage.event_type) return;

      switch (tavusMessage.event_type) {
        case "conversation.utterance":
          if (tavusMessage.properties?.role === 'user') {
            // setPauseState(0); // Add if you implement pause detection
          }
          break;
        case "conversation.replica.started_speaking":
          setIsAiSpeaking(true);
          break;
        case "conversation.replica.stopped_speaking":
          setIsAiSpeaking(false);
          break;
        default:
      }
    },
    [conversationStarted, callObject, interviewContexts, customGreeting, isRecordingStarting, sessionId, userName, interviewName, isUserSpeaking, isYesNoMessage]
  );

  useDailyEvent("app-message", handleAppMessage);

  useDailyEvent("participant-joined", useCallback((event) => {
    if (event.participant.session_id !== localParticipant?.session_id) {
      setAiJoined(true);
    }
  }, [localParticipant?.session_id]));

  useDailyEvent("participant-left", useCallback((event) => {
  }, []));

  useDailyEvent("left-meeting", useCallback(() => {
    // Clean up all media streams regardless of state to prevent browser showing camera/mic as in use
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => {
        track.stop();
      });
      setMediaStream(null);
    }

    // Additional cleanup to ensure browser releases camera/mic
    setTimeout(async () => {
      try {
        // This will fail but triggers browser to clean up any remaining media access
        const cleanupStream = await navigator.mediaDevices.getUserMedia({ audio: false, video: false });
        cleanupStream.getTracks().forEach(track => {
          track.stop();
        });
      } catch (error) {
      }
    }, 100);

    // Reset connection states
    setDailyJoined(false);
    setAiJoined(false);
  }, [mediaStream]));

  useDailyEvent("recording-started", useCallback((event) => {
    setIsStartingRecording(false);
    setShowConfirmationModal(false);

    if (sessionId && callObject) {
      try {
        // Start recording now that user has joined
        callObject.setLocalAudio(true);

        // Mark interview start time
        setInterviewStartTime(Date.now());

        setShowVideoUI(true); // Show the main UI
        setIsActive(true); // Set interview as active to hide loading overlay
        setIsRecordingStarting(false);

        // Set initial context (Step 0)
        updateConversationContext(interviewContexts[0]);
        setInterviewStep(0); // Explicitly set step to 0

        // wait 1 second
        setTimeout(() => {
          makeAiSay(`${customGreeting ? customGreeting : "Hey, thanks for joining the call today. Are you ready for your MTAI interview?"}`)
        }, 1000)
      } catch (error) {
        setApiErrorMessage("Failed to start interview session");
      }
    } else {
      setApiErrorMessage("Cannot start interview: Session ID missing.");
    }
  }, [sessionId, callObject, interviewContexts, userName, isRecordingStarting, customGreeting, isTestInterview]));

  // Effect to progress interview steps automatically
  useEffect(() => {
    // Create dynamic step durations array from sections prop
    // Index 0 is initial delay (0), then each section's duration
    const stepDurations = [0, ...sections.map(section => section.duration)];

    let timeoutId: NodeJS.Timeout | null = null;

    // Only run the timer when the main video UI is shown and the call is active
    if (showVideoUI && callObject && sessionId && callObject.meetingState() === 'joined-meeting') {
      const currentStep = interviewStep;
      const nextStep = currentStep + 1;

      // Check if there is a next step context
      if (nextStep < interviewContexts.length) {
        // Get the duration for the *current* step (time until we move to the *next* step)
        // Use stepDurations[nextStep] because stepDurations[1] is duration for step 0, etc.
        const durationInSeconds = stepDurations[nextStep];

        if (durationInSeconds > 0) {
          timeoutId = setTimeout(() => {
            // Update context to the next step's context
            updateConversationContext(interviewContexts[nextStep]);
            // Update the state to reflect the new current step
            setInterviewStep(nextStep);
            setCurrentSection(nextStep); // Also update current section for UI
          }, durationInSeconds * 1000);

        } else {
        }
      } else {
        // We have completed the last step
        const lastStepDuration = stepDurations[interviewContexts.length];
        if (lastStepDuration > 0) {
          timeoutId = setTimeout(() => {
            startConclusion()
          }, lastStepDuration * 1000);
        } else {
        }
      }
    }

    // Cleanup function: Clear the timeout when the effect re-runs or component unmounts
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showVideoUI, callObject, sessionId, interviewStep, sections, interviewContexts]);

  // Initialize media stream (without automatic permission request)
  const initializeMediaStream = useCallback(async () => {
    try {
      setIsBrowserCompatible(isBrowserSupported());

      // Only enumerate devices, don't request permissions automatically
      await enumerateDevices();
    } catch (error) {
      setApiErrorMessage('Failed to initialize media. Please check your browser compatibility.');
    }
  }, [enumerateDevices]);

  // Handle start interview button click
  const handleStartInterviewClick = () => {
    if (!canStartInterview) {
      return;
    }
    setShowConfirmationModal(true);
  };

  // Handle confirmation modal confirm
  const handleConfirmInterview = async () => {
    try {
      setIsStartingRecording(true);
      await callObject?.startRecording({
        type: "cloud",
      });
      // Modal will be closed when recording-started event fires
    } catch (error) {
      setIsStartingRecording(false);
      setApiErrorMessage('Failed to start recording. Please try again.');
    }
  };

  // Toggle mute
  const toggleMute = () => {
    const newMutedState = !isMuted;

    // Use Daily.co's setLocalAudio API (proper way to mute/unmute)
    if (callObject) {
      callObject.setLocalAudio(!newMutedState); // false = muted, true = unmuted
    }

    // Also disable tracks at browser level for extra privacy
    if (mediaStream) {
      const audioTracks = mediaStream.getAudioTracks();

      audioTracks.forEach((track, index) => {
        track.enabled = !newMutedState; // When muting (newMutedState=true), disable track
      });
    }

    setIsMuted(newMutedState);
    // Ref will be updated by the useEffect
  };

  // Toggle video
  const toggleVideo = () => {
    const newVideoState = !isVideoOn;

    // Use Daily.co's setLocalVideo API
    if (callObject) {
      callObject.setLocalVideo(newVideoState); // true = on, false = off
    }

    // Also control tracks at browser level
    if (mediaStream) {
      const videoTracks = mediaStream.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = newVideoState;
      });
    }

    setIsVideoOn(newVideoState);
  };

  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Initialize on mount
  useEffect(() => {
    initializeMediaStream();

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      if (sectionTimerRef.current) window.clearTimeout(sectionTimerRef.current);
      if (autoEndTimeoutRef.current) {
        clearTimeout(autoEndTimeoutRef.current);
        autoEndTimeoutRef.current = null;
      }
    };
  }, []); // Empty dependency array to run only once on mount

  // Cleanup media stream on unmount only (not on every mediaStream change)
  useEffect(() => {
    return () => {
      // Only cleanup on component unmount
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => {
          track.stop();
        });
      }
    };
  }, []); // Empty dependency array - only runs on mount/unmount

  // Enhanced session cleanup on browser events (tab close, navigation, etc.)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Clean up media streams immediately
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => {
          track.stop();
        });
      }

      // Leave Daily.co call if connected
      if (callObject && callObject.meetingState() === 'joined-meeting') {
        callObject.leave().catch(() => {});
      }

      // Show confirmation dialog for active interviews
      if (isActive && showVideoUI) {
        event.preventDefault();
        event.returnValue = 'You have an active interview. Are you sure you want to leave?';
        return 'You have an active interview. Are you sure you want to leave?';
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && isActive && showVideoUI) {
        // Don't cleanup immediately as user might come back
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // If it's a Daily.co or media-related error, attempt cleanup
      if (event.reason && typeof event.reason === 'string') {
        const reason = event.reason.toLowerCase();
        if (reason.includes('daily') || reason.includes('media') || reason.includes('pipecat')) {
          cleanupAndExit();
        }
      }
    };

    const handleError = (event: ErrorEvent) => {
      // If it's a critical media or Daily.co error, attempt cleanup
      if (event.error && event.error.message) {
        const message = event.error.message.toLowerCase();
        if (message.includes('daily') || message.includes('media') || message.includes('pipecat')) {
          setApiErrorMessage(`Connection error: ${event.error.message}`);
        }
      }
    };

    // Attach event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      // Clean up event listeners
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, [mediaStream, callObject, sessionId, isActive, showVideoUI]);

  // Auto-create conversation when conditions are met - following old component pattern
  useEffect(() => {
    if (!blockInterview && authToken && agentName && status === 'idle' && !interviewExpired) {
      createConversation();
    }
  }, [blockInterview, authToken, agentName, status, createConversation, interviewExpired]);

  // Start countdown when conditions are met
  useEffect(() => {
    if (canStartInterview && !countdownActive && !showVideoUI && !interviewExpired) {
      setCountdownActive(true);

      const timer = setInterval(() => {
        setCountdownSeconds(prev => {
          if (prev <= 1) {
            setInterviewExpired(true);
            setCountdownActive(false);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [canStartInterview, countdownActive, showVideoUI, interviewExpired]);

  // Test mode banner component
  const TestModeBanner = () => {
    if (!isTestInterview) return null;

    return (
      <div className="bg-blue-600 text-white px-4 py-2 text-center font-semibold">
        🧪 TEST MODE - This interview is for testing purposes only
      </div>
    );
  };

  // Format countdown time
  const formatCountdownTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show error state if not compatible
  if (!isBrowserCompatible) {
    return (
      <div className="fixed inset-0 z-40 bg-black/90 backdrop-blur-sm pt-32">
        <div className="h-full flex items-center justify-center">
          <div className="bg-gray-900/60 backdrop-blur-2xl border border-gray-700/40 rounded-2xl p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Browser Not Supported</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Your browser doesn't support the required features for video interviews.
              Please use Chrome, Firefox, Safari, or Edge.
            </p>
            <button
              onClick={onEndInterview}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper to make AI speak (if needed)
  const makeAiSay = (line: string) => {
    if (callObject && sessionId && callObject.meetingState() === 'joined-meeting') {
      const interaction = {
        message_type: "conversation",
        event_type: "conversation.echo", // Using echo for direct TTS
        conversation_id: sessionId,
        properties: {
          text: line
        },
      };
      callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
    } else {
    }
  };

  // Enhanced cleanup function to properly stop media streams and reset state
  const cleanupAndExit = useCallback(async () => {
    // CRITICAL FIX: If interview never started (user cancelled from waiting room),
    // notify backend so status doesn't stay as "in_progress"/"Under Review"
    if (sessionId && !isActive && !showVideoUI) {
      try {
        // Send empty transcript to mark interview as cancelled/no-show
        await fetch(API_CONFIG.endpoints.interviews.finish(sessionId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            transcript: [],
            cancelled: true  // Flag to indicate this was cancelled, not completed
          })
        });
      } catch (error) {
        // Silent fail - user is leaving anyway
      }
    }

    // Stop all media tracks more thoroughly
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => {
        track.stop();
      });
      setMediaStream(null);
    }

    // Also stop any additional media streams that might exist
    try {
      const allStreams = await navigator.mediaDevices.getUserMedia({ audio: false, video: false });
      allStreams.getTracks().forEach(track => {
        track.stop();
      });
    } catch (error) {
    }

    // End Pipecat conversation and leave Daily call
    if (callObject && callObject.meetingState() === 'joined-meeting') {
      // Leave Daily call
      try {
        await callObject.leave();
      } catch (error) {
      }
    }

    // Destroy Daily call object if it exists
    try {
      if (callObject) {
        await callObject.destroy();
      }
    } catch (error) {
    }

    // Clear timers
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (sectionTimerRef.current) {
      window.clearTimeout(sectionTimerRef.current);
      sectionTimerRef.current = null;
    }

    // Reset conversation and UI states, but preserve camera/mic permissions for waiting room
    setStatus('idle');
    setIsActive(false);
    setShowVideoUI(false);
    setShowConfirmationModal(false);
    setConversationUrl(null);
    setConversationToken(null);
    setAiJoined(false);
    setDailyJoined(false);

    onEndInterview();
  }, [mediaStream, callObject, timerRef, sectionTimerRef, onEndInterview, sessionId, isActive, showVideoUI]);

  const startConclusion = () => {
    if (callObject && sessionId && callObject.meetingState() === 'joined-meeting') {
      const interaction = {
        message_type: "conversation",
        event_type: "conversation.end",
        conversation_id: sessionId,
        properties: {
        },
      };
      callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)

    } else {
    }
  };

  const endAiCall = async () => {
    // Prevent double execution
    if (isCompletingInterview) {
      return;
    }

    setIsCompletingInterview(true);

    // Clear any pending auto-end timeout
    if (autoEndTimeoutRef.current) {
      clearTimeout(autoEndTimeoutRef.current);
      autoEndTimeoutRef.current = null;
    }

    // CRITICAL: Send interview completion to backend BEFORE navigating
    if (sessionId) {
      try {
        // Transform conversationMessages to backend format
        const transcript = conversationMessages
          .filter(msg => msg.isComplete !== false) // Only include complete messages
          .map(msg => ({
            role: msg.type === 'ai' ? 'assistant' : 'user',
            content: msg.content
          }));

        const response = await fetch(API_CONFIG.endpoints.interviews.finish(sessionId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ transcript })
        });

        if (response.ok) {
        } else {
        }
      } catch (error) {
      }
    }

    // Now navigate away
    onEndInterview();

    // Stop all media tracks first
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => {
        track.stop();
      });
      setMediaStream(null);
    }

    // Also stop any additional media streams that might exist
    try {
      const allStreams = await navigator.mediaDevices.getUserMedia({ audio: false, video: false });
      allStreams.getTracks().forEach(track => {
        track.stop();
      });
    } catch (error) {
    }

    // End Pipecat conversation and leave Daily call
    if (callObject && sessionId && callObject.meetingState() === 'joined-meeting') {
      try {
        // Now leave the Daily call
        await callObject.leave();
      } catch (error) {
      }
    } else if (callObject && callObject.meetingState() === 'joined-meeting') {
      // Fallback: just leave Daily call if no conversation ID
      try {
        await callObject.leave();
      } catch (error) {
      }
    }

    // Destroy Daily call object if it exists
    try {
      if (callObject) {
        await callObject.destroy();
      }
    } catch (error) {
    }

    // Clear timers
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (sectionTimerRef.current) {
      window.clearTimeout(sectionTimerRef.current);
      sectionTimerRef.current = null;
    }

    // Reset states
    setStatus('completed');
    setIsActive(false);
    setCameraAccessGranted(false);
    setMicAccessGranted(false);
    setShowVideoUI(false);
    // setConversationId(null);
    setConversationUrl(null);
    setConversationToken(null);
    setAiJoined(false);
    setDailyJoined(false);

    // onEndInterview() already called at the beginning to immediately navigate away
  };

  const updateConversationContext = (context: string) => {
    if (callObject && sessionId) {
      const interaction = {
        message_type: "conversation",
        event_type: "conversation.overwrite_llm_context", // Using echo for direct TTS
        conversation_id: sessionId,
        properties: {
          context: context
        },
      };
      callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
    } else {
    }
  };

  // Timer effect for counting down interview time
  useEffect(() => {
    if (isActive && showVideoUI && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 1;
          return Math.max(0, newTime);
        });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isActive, showVideoUI, timeRemaining]);

  return (
    <div className="fixed inset-0 z-40 overflow-hidden">
      {/* Dark Grayish Background with FloatingParticles */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black">
        <FloatingParticles />
      </div>
      <TestModeBanner />

      {!showVideoUI ? (
        // Enhanced Waiting Room UI
        <div className="relative z-10 h-screen overflow-y-auto">
          <div className="min-h-full flex items-center justify-center p-4 py-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="w-full max-w-7xl mx-auto"
            >
              {/* Futuristic Container */}
              <div className="relative">
                {/* Glowing border effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-gray-600/10 via-gray-500/15 to-gray-600/10 rounded-3xl blur-sm" />

                <div className="relative bg-gray-900/40 backdrop-blur-2xl border border-gray-700/30 rounded-3xl overflow-hidden shadow-2xl">
                  {/* Enhanced Header - Centered layout */}
                  <div className="relative p-4 sm:p-8 border-b border-gray-700/30">
                    {/* Header glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-700/5 via-gray-600/8 to-gray-700/5" />

                    <div className="relative">
                      <div className="flex flex-col items-center text-center mb-4 sm:mb-6">
                        {/* Big MTAI Logo */}
                        <div className="relative mb-4">
                          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-2xl flex items-center justify-center shadow-2xl shadow-black/80 ring-1 ring-white/10">
                            <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className="w-10 h-10 sm:w-12 sm:h-12" />
                          </div>
                          <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                            <div className="w-2 h-2 bg-white rounded-full" />
                          </div>
                        </div>

                        {/* Interview Title - Centered */}
                        <div>
                          <h1 className="text-xl sm:text-3xl font-bold text-white mb-1">
                            Interview for {interviewName}
                          </h1>
                          <p className="text-gray-300 text-sm sm:text-base">
                            AI-Powered Interview Experience
                          </p>
                        </div>
                      </div>

                      {/* Status Cards - Smaller on mobile */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-4">
                        <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border transition-all duration-300 ${cameraAccessGranted
                            ? 'bg-green-500/5 border-green-500/30'
                            : 'bg-gray-500/5 border-gray-500/30'
                          }`}>
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <div className={`p-1.5 sm:p-2 rounded-md sm:rounded-lg ${cameraAccessGranted ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                              <Video className={`w-4 h-4 sm:w-5 sm:h-5 ${cameraAccessGranted ? 'text-green-400' : 'text-gray-300'}`} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-white font-medium text-sm sm:text-base">Camera</p>
                              <p className={`text-xs sm:text-sm ${cameraAccessGranted ? 'text-green-400' : 'text-gray-300'}`}>
                                {cameraAccessGranted ? 'Ready' : 'Needed'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border transition-all duration-300 ${micAccessGranted
                            ? 'bg-green-500/5 border-green-500/30'
                            : 'bg-gray-500/5 border-gray-500/30'
                          }`}>
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <div className={`p-1.5 sm:p-2 rounded-md sm:rounded-lg ${micAccessGranted ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                              <Mic className={`w-4 h-4 sm:w-5 sm:h-5 ${micAccessGranted ? 'text-green-400' : 'text-gray-300'}`} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-white font-medium text-sm sm:text-base">Microphone</p>
                              <p className={`text-xs sm:text-sm ${micAccessGranted ? 'text-green-400' : 'text-gray-300'}`}>
                                {micAccessGranted ? 'Ready' : 'Needed'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border transition-all duration-300 ${aiJoined
                            ? 'bg-green-500/5 border-green-500/30'
                            : 'bg-gray-500/5 border-gray-500/30'
                          }`}>
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <div className={`p-1.5 sm:p-2 rounded-md sm:rounded-lg ${aiJoined ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                              <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className={`w-4 h-4 sm:w-5 sm:h-5 ${aiJoined ? 'opacity-100' : 'opacity-60'}`} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-white font-medium text-sm sm:text-base">AI Interviewer</p>
                              <p className={`text-xs sm:text-sm ${aiJoined ? 'text-green-400' : 'text-gray-300'}`}>
                                {aiJoined ? 'Ready' : 'Connecting...'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Permission Request Button */}
                      {(!cameraAccessGranted || !micAccessGranted) && !permissionRequested && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-6"
                        >
                          <button
                            onClick={requestPermissions}
                            className="w-full px-6 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-3 shadow-lg shadow-amber-500/25"
                          >
                            <Shield className="w-5 h-5" />
                            Grant Camera & Microphone Access
                          </button>
                          <p className="text-center text-sm text-gray-400 mt-2">
                            Click to allow camera and microphone access for your interview
                          </p>
                        </motion.div>
                      )}

                      {/* Countdown Display - Smaller on mobile */}
                      {countdownActive && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 sm:mt-6 p-3 sm:p-4 bg-red-500/5 backdrop-blur-sm border border-red-500/30 rounded-lg sm:rounded-xl"
                        >
                          <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                            <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-red-400 animate-pulse" />
                            <span className="text-white text-sm sm:text-lg font-medium">
                              Session expires in:
                            </span>
                            <span className="text-red-400 text-base sm:text-xl font-bold">
                              {formatCountdownTime(countdownSeconds)}
                            </span>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>

                  {/* Main Content Area - More compact on mobile */}
                  <div className="p-4 sm:p-8 space-y-4 sm:space-y-8 lg:grid lg:grid-cols-2 lg:gap-8 lg:space-y-0">
                    {/* Video Preview */}
                    <div className="space-y-2 sm:space-y-4">
                      <h3 className="hidden sm:block text-xl font-semibold text-white">Camera Preview</h3>
                      <div className="relative bg-gray-900 rounded-lg overflow-hidden aspect-video">
                        <video
                          ref={videoRef}
                          autoPlay
                          muted
                          playsInline
                          className="w-full h-full object-cover"
                        />
                        {!cameraAccessGranted && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                            <div className="text-center text-white">
                              <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p>Camera access required</p>
                            </div>
                          </div>
                        )}
                        {isUserSpeaking && (
                          <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm">
                            Speaking...
                          </div>
                        )}
                      </div>

                      {/* Device Status */}
                      <div className="space-y-3">
                        <div className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${cameraAccessGranted ? 'text-green-400 bg-green-500/5' : 'text-gray-300 bg-amber-500/5'}`}>
                          <Video className="w-5 h-5" />
                          <span className="text-sm font-medium">Camera: {cameraAccessGranted ? 'Ready' : 'Permission needed'}</span>
                        </div>
                        <div className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${micAccessGranted ? 'text-green-400 bg-green-500/5' : 'text-gray-300 bg-amber-500/5'}`}>
                          <Mic className="w-5 h-5" />
                          <span className="text-sm font-medium">Microphone: {micAccessGranted ? 'Ready' : 'Permission needed'}</span>
                        </div>
                        <div className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${aiJoined ? 'text-green-400 bg-green-500/5' : 'text-gray-300 bg-yellow-500/5'}`}>
                          <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className="w-5 h-5" />
                          <span className="text-sm font-medium">AI Interviewer: {aiJoined ? 'Ready' : 'Connecting...'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Device Settings & Controls */}
                    <div className="space-y-6">
                      {/* Device Selection */}
                      <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-white">Device Settings</h3>

                        {/* Microphone Selection */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-300">Microphone</label>
                          <select
                            value={selectedAudioInputId}
                            onChange={(e) => {
                              const device = audioInputDevices.find(d => d.deviceId === e.target.value);
                              if (device) handleMicrophoneSelect(device);
                            }}
                            className="w-full px-4 py-3 border border-gray-600/30 rounded-xl bg-gray-800/20 backdrop-blur-sm text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                          >
                            {audioInputDevices.map(device => (
                              <option key={device.deviceId} value={device.deviceId} className="bg-gray-800">
                                {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Speaker Selection */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-300">Speaker</label>
                          <select
                            value={selectedAudioOutputId}
                            onChange={(e) => {
                              const device = audioOutputDevices.find(d => d.deviceId === e.target.value);
                              if (device) handleSpeakerSelect(device);
                            }}
                            className="w-full px-4 py-3 border border-gray-600/30 rounded-xl bg-gray-800/20 backdrop-blur-sm text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                          >
                            {audioOutputDevices.map(device => (
                              <option key={device.deviceId} value={device.deviceId} className="bg-gray-800">
                                {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Camera Selection */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-300">Camera</label>
                          <select
                            value={selectedVideoId}
                            onChange={(e) => {
                              const device = videoDevices.find(d => d.deviceId === e.target.value);
                              if (device) handleCameraSelect(device);
                            }}
                            className="w-full px-4 py-3 border border-gray-600/30 rounded-xl bg-gray-800/20 backdrop-blur-sm text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                          >
                            {videoDevices.map(device => (
                              <option key={device.deviceId} value={device.deviceId} className="bg-gray-800">
                                {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Interview Info */}
                      <div className="bg-gray-800/20 backdrop-blur-sm border border-gray-600/30 rounded-xl p-6">
                        <h4 className="font-semibold mb-4 text-white text-lg">Interview Details</h4>
                        <div className="space-y-3 text-sm">
                          <div className="flex items-center justify-between py-2 border-b border-gray-600/30">
                            <span className="text-gray-300">Duration:</span>
                            <span className="text-white font-medium">{maxDuration} minutes</span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b border-gray-600/30">
                            <span className="text-gray-300">Sections:</span>
                            <span className="text-white font-medium">{sections.length}</span>
                          </div>
                          <div className="flex items-center justify-between py-2">
                            <span className="text-gray-300">AI Interviewer:</span>
                            <span className="text-white font-medium">{aiName}</span>
                          </div>
                        </div>
                      </div>

                      {/* Error Messages */}
                      {apiErrorMessage && (
                        <div className="bg-red-500/5 backdrop-blur-sm border border-red-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-3 text-red-400">
                            <AlertTriangle className="w-5 h-5" />
                            <span className="text-sm font-semibold">Error</span>
                          </div>
                          <p className="text-sm text-red-300 mt-2">{apiErrorMessage}</p>

                          {/* Permission Request Button for permission-related errors */}
                          {(apiErrorMessage.includes('permission') || apiErrorMessage.includes('camera') || apiErrorMessage.includes('microphone')) && !permissionRequested && (
                            <motion.button
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              onClick={requestPermissions}
                              className="mt-4 w-full px-4 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25"
                            >
                              <Shield className="w-4 h-4" />
                              Grant Camera & Microphone Access
                            </motion.button>
                          )}
                        </div>
                      )}

                      {interviewExpired && (
                        <div className="bg-red-500/5 backdrop-blur-sm border border-red-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-3 text-red-400">
                            <AlertTriangle className="w-5 h-5" />
                            <span className="text-sm font-semibold">Session Expired</span>
                          </div>
                          <p className="text-sm text-red-300 mt-2">
                            The interview session has expired. Please refresh and try again.
                          </p>
                        </div>
                      )}

                      {/* Start Button */}
                      <div className="space-y-3 pt-4">
                        <button
                          onClick={handleStartInterviewClick}
                          disabled={!canStartInterview}
                          title={getButtonTooltip()}
                          className={`w-full px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${canStartInterview
                              ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg shadow-amber-500/25'
                              : 'bg-gray-800/20 text-gray-400 cursor-not-allowed border border-gray-600/30'
                            }`}
                        >
                          {status === 'connecting' ? (
                            <div className="flex items-center justify-center gap-3">
                              <Loader className="w-5 h-5 animate-spin" />
                              Connecting to Interview...
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-3">
                              <Play className="w-5 h-5" />
                              Start Interview
                            </div>
                          )}
                        </button>

                        {!canStartInterview && (
                          <p className="text-sm text-gray-400 text-center px-4">
                            {getButtonTooltip()}
                          </p>
                        )}
                      </div>

                      {/* Cancel Button */}
                      <button
                        onClick={cleanupAndExit}
                        className="w-full px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                      >
                        Cancel Interview
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      ) : (
        // Main Interview UI
        <div className="fixed inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black z-50 flex flex-col">
          <FloatingParticles />
          {/* Header - Mobile optimized */}
          <div className="bg-gray-900/30 backdrop-blur-2xl border-b border-gray-700/30 p-3 sm:p-4 flex-shrink-0 relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${isActive ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`} />
                  <span className="text-white font-medium text-sm sm:text-base truncate">{interviewName}</span>
                </div>
                {isActive && (
                  <div className="flex items-center gap-1.5 sm:gap-2 text-white">
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="font-mono text-sm sm:text-base">{formatTime(timeRemaining)}</span>
                  </div>
                )}
              </div>

              {/* Better styled section indicator */}
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm bg-white/5 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border border-white/10 text-gray-300 truncate max-w-[200px] sm:max-w-none">
                  <span className="text-amber-400 font-semibold">Section {currentSection + 1}/{sections.length}</span>
                  <span className="hidden sm:inline">: {sections[currentSection]?.title}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Main Content - YouTube-style on mobile (vertical), horizontal on desktop */}
          <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden">
            {/* Video Area - Full width on mobile, fills remaining space on desktop */}
            <div className="flex-1 relative min-h-0 transition-all duration-300">
              {/* User's video using DailyVideo component */}
              {localParticipant ? (
                <>
                  <DailyVideo
                    key={localParticipant.session_id}
                    sessionId={localParticipant.session_id}
                    type="video"
                    className="w-full h-full object-cover"
                    automirror
                    style={{ display: isVideoOn && localParticipant.video ? "block" : "none" }}
                  />
                  {/* Show placeholder if video is off */}
                  {(!isVideoOn || !localParticipant.video) && (
                    <div className="w-full h-full flex items-center justify-center bg-gray-900">
                      <div className="h-32 w-32 rounded-full bg-gray-700 flex items-center justify-center text-white text-6xl font-bold">
                        {userName?.substring(0, 1)?.toUpperCase() || "U"}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-900 text-white">
                  <Loader className="w-8 h-8 animate-spin mr-2" />
                  Loading video...
                </div>
              )}

              {/* AI Avatar Area */}
              <div className="absolute top-4 right-4 w-32 h-24 bg-gray-800/30 backdrop-blur-xl rounded-lg border border-gray-600/30 overflow-hidden">
                <div className="w-full h-full flex items-center justify-center relative">
                  <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className="w-8 h-8 opacity-60" />

                  {isAiSpeaking && (
                    <div className="absolute inset-0 border-2 border-blue-400 rounded-lg animate-pulse" />
                  )}

                  <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1 rounded">
                    {aiName}
                  </div>
                </div>
              </div>

              {/* User Speaking Indicator - Only show when NOT muted */}
              <AnimatePresence>
                {isUserSpeaking && !isMuted && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    className="absolute bottom-20 sm:bottom-4 left-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-xl border border-green-400/30 text-white px-4 py-2.5 rounded-full flex items-center gap-2.5 shadow-lg"
                  >
                    <div className="relative flex items-center justify-center">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      <div className="absolute w-2 h-2 bg-green-400 rounded-full animate-ping" />
                    </div>
                    <span className="text-sm font-medium">You're speaking</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Controls */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                <div className="flex items-center gap-4 bg-gray-800/30 backdrop-blur-2xl border border-gray-600/30 rounded-full px-6 py-3 shadow-xl">
                  <button
                    onClick={toggleMute}
                    className={`p-3 rounded-full transition-colors ${isMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700/50 hover:bg-gray-600/50'
                      }`}
                  >
                    {isMuted ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-white" />}
                  </button>

                  <button
                    onClick={toggleVideo}
                    className={`p-3 rounded-full transition-colors ${!isVideoOn ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700/50 hover:bg-gray-600/50'
                      }`}
                  >
                    {isVideoOn ? <Video className="w-5 h-5 text-white" /> : <VideoOff className="w-5 h-5 text-white" />}
                  </button>

                  <button
                    onClick={() => setShowTranscript(!showTranscript)}
                    className="p-3 rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
                  >
                    <MessageCircle className="w-5 h-5 text-white" />
                  </button>

                  <button
                    onClick={endAiCall}
                    className="p-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-white transform rotate-[135deg]" />
                  </button>
                </div>
              </div>

              {/* Starting Overlay */}
              {!isActive && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="text-center text-white">
                    <Loader className="w-12 h-12 animate-spin mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Starting Interview</h3>
                    <p className="text-gray-300">Please wait while we initialize...</p>
                    {apiErrorMessage && (
                      <p className="text-red-400 mt-2">{apiErrorMessage}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Ending Call Overlay */}
              {isCompletingInterview && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
                >
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="text-center text-white max-w-sm mx-4"
                  >
                    <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                      <div className="relative mb-6">
                        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full flex items-center justify-center">
                          <Loader className="w-10 h-10 text-amber-400 animate-spin" />
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-24 h-24 border-4 border-amber-500/20 rounded-full animate-ping" />
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                        Ending Interview
                      </h3>
                      <p className="text-gray-300 text-sm leading-relaxed mb-2">
                        Saving your responses and wrapping up...
                      </p>
                      <p className="text-gray-400 text-xs">
                        This may take a moment
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </div>

            {/* Transcript Panel */}
            <AnimatePresence>
              {showTranscript && (
                <TranscriptionPanel
                  onClose={() => setShowTranscript(false)}
                  aiName={aiName}
                  userName={userName}
                  colorScheme="default"
                  aiLogoSrc={undefined}
                  messages={conversationMessages}
                  isAiSpeaking={isAiSpeaking}
                  isUserSpeaking={isUserSpeaking}
                  isMuted={isMuted}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4 dark:text-white">
              {isStartingRecording ? "Starting Interview..." : "Confirm Interview Start"}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {isStartingRecording
                ? "Please wait while we prepare your interview session. This may take a few moments."
                : `Are you ready to begin your ${interviewName}? This will start recording and you'll have ${maxDuration} minutes to complete the interview.`
              }
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmationModal(false)}
                disabled={isStartingRecording}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmInterview}
                disabled={isStartingRecording}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
              >
                {isStartingRecording ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Joining...
                  </>
                ) : (
                  "Start Interview"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Call Popup */}
      {showLeaveCallPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md mx-4 shadow-2xl border border-gray-200 dark:border-gray-700"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Interview Complete!
              </h3>

              <p className="text-gray-600 dark:text-gray-400 mb-6">
                You may leave the call now. Thank you for your time!
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowLeaveCallPopup(false);
                    endAiCall();
                  }}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-4 py-2 rounded-lg transition-colors font-semibold"
                >
                  End Call
                </button>

                <button
                  onClick={() => setShowLeaveCallPopup(false)}
                  className="flex-1 bg-gray-700/30 hover:bg-gray-600/30 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Stay
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Daily.co Audio Component for remote participants */}
      <DailyAudio />
    </div>
  );
};
